import { useCallback, useMemo, useState } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import { Entity, EntityKey } from '@common/api';
import { FlexColCentered } from '@components/layout/Flex';
import { LoadingSpinner } from '@components/loading/LoadingSpinner';
import { Modal } from '@components/modal/Modal';
import { useModal } from '@components/modal/ModalProvider';
import { useEntities, useMutateEntities } from '@data/entity/entity';
import useTheme from '@base/useTheme';

export type DeleteTagsModalProps = {
    tagKeys: EntityKey[];
    onDeleted?: () => void;
    onCancel?: () => void;
};

export type DeleteTagsModalInnerProps = {
    tags: Entity[];
    onDeleted?: () => void;
    onCancel?: () => void;
};

export const DeleteTagsModal = (props: DeleteTagsModalProps) => {
    const { tagKeys, onDeleted, onCancel } = props;
    const tags = useEntities(tagKeys).filter((e) => !!e) as Entity[];
    return <DeleteTagsModalInner tags={tags} onDeleted={onDeleted} onCancel={onCancel} />;
};

export const DeleteTagsModalInner = (props: DeleteTagsModalInnerProps) => {
    const { tags, onDeleted } = props;

    const { LL } = useI18nContext();
    const { hideModal } = useModal();
    const theme = useTheme();

    const [loading, setLoading] = useState(false);

    const mutateEntities = useMutateEntities();

    const error = useMemo(() => {
        const tagsWithConnections: string[] = [];
        for (const tag of tags) {
            if (tag.links?.connections?.length) {
                tagsWithConnections.push(tag.meta.name);
            }
        }

        if (tagsWithConnections.length === 0) {
            return null;
        } else if (tagsWithConnections.length === 1) {
            return 'Careful, this member has connections';
        } else {
            return 'Careful, members have connections';
        }
    }, [tags]);

    const handleDelete = useCallback(async () => {
        setLoading(true);
        await mutateEntities({ deleted: tags });
        setLoading(false);
        hideModal();
        onDeleted?.();
    }, [mutateEntities, tags, hideModal, onDeleted]);

    const getTagLabel = (tag: Entity) => [tag.meta.name, tag.meta.description].filter((s) => s?.length).join(' ');
    const tagsLabel = <b>{tags.map(getTagLabel).join(', ')}</b>;

    const errorNode =
        error != null ? (
            <div style={{ paddingTop: theme.space.l }} className="red center">
                {error}
            </div>
        ) : null;

    const title = (
        <>
            {LL.DELETE()} {tagsLabel} {LL.DELETE_TARGET_AND_RELATED_DATA()}
        </>
    );

    return (
        <Modal.Content width={650}>
            <Modal.Header title={title} />
            <Modal.Body>
                {loading ? (
                    <FlexColCentered style={{ width: '100%', height: '100%' }}>
                        <LoadingSpinner />
                    </FlexColCentered>
                ) : (
                    errorNode ?? title
                )}
            </Modal.Body>
            <Modal.Footer cancelLabel={LL.CANCEL()} done={handleDelete} doneLabel={LL.DELETE()} doneColor="error" />
        </Modal.Content>
    );
};
